import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
  Stack,
  InputAdornment,
  Chip,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Tabs,
  Tab,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  ViewList as ViewListIcon,
  ViewKanban as ViewKanbanIcon,
} from "@mui/icons-material";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useDroppable } from "@dnd-kit/core";
import { useAppSelector, useAppDispatch, RootState } from "../store";
import {
  fetchTasks,
  createTask,
  updateTask,
  deleteTask,
  toggleTaskComplete,
  updateTaskStatus,
  clearError,
} from "../store/slices/tasksSlice";
import { Task } from "../services/tasksService";
import { useSelector } from "react-redux";
import usersService from "../services/usersService";
import { fetchCases } from "../store/slices/casesSlice";
import { useToast } from "../hooks/useToast";
import StatusBadge, { getStatusFilterOptions } from "../components/common/StatusBadge";
import { useConfirm } from "../contexts/ConfirmDialogContext";

interface FormData {
  title: string;
  description: string;
  assignee_id: string;
  case_id?: number;
  priority: string;
  status: string;
  due_date: string;
}

const initialFormData: FormData = {
  title: "",
  description: "",
  assignee_id: "", // Default to current user
  case_id: undefined,
  priority: "medium",
  status: "pending",
  due_date: "",
};

// Sortable Task Card Component
const SortableTaskCard: React.FC<{
  task: Task;
  onEdit: (task: Task) => void;
  onDelete: (id: number) => void;
  onToggleComplete: (id: number) => void;
}> = ({ task, onEdit, onDelete, onToggleComplete }) => {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getPriorityColor = (
    priority: string,
  ): "default" | "primary" | "warning" | "error" | "info" => {
    const map: Record<
      string,
      "default" | "primary" | "warning" | "error" | "info"
    > = {
      low: "info",
      medium: "primary",
      high: "warning",
      urgent: "error",
    };
    return map[priority] || "default";
  };


  return (
    <Paper
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      sx={{
        p: 2,
        mb: 1,
        cursor: "grab",
        "&:hover": {
          boxShadow: 2,
        },
        bgcolor: task.completed ? "grey.50" : "background.paper",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Checkbox
          checked={task.completed}
          onChange={() => onToggleComplete(task.id)}
          size="small"
          sx={{ mt: -0.5 }}
        />
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              textDecoration: task.completed ? "line-through" : "none",
              color: task.completed ? "text.secondary" : "text.primary",
              mb: 0.5,
            }}
          >
            {task.title}
          </Typography>
          {task.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              {task.description.length > 100
                ? `${task.description.substring(0, 100)}...`
                : task.description}
            </Typography>
          )}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Chip
              label={task.priority}
              color={getPriorityColor(task.priority)}
              size="small"
            />
            {task.due_date && (
              <Typography variant="caption" color="text.secondary">
                {t("tasks.due")}: {new Date(task.due_date).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Tooltip title={t("common.edit")}>
            <IconButton size="small" onClick={() => onEdit(task)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("common.delete")}>
            <IconButton
              size="small"
              color="error"
              onClick={() => onDelete(task.id)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
};

// Droppable Kanban Column Component
const DroppableKanbanColumn: React.FC<{
  title: string;
  status: string;
  tasks: Task[];
  onEdit: (task: Task) => void;
  onDelete: (id: number) => void;
  onToggleComplete: (id: number) => void;
}> = ({ title, status, tasks, onEdit, onDelete, onToggleComplete }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: status,
  });

  return (
    <Paper 
      key={status}
      ref={setNodeRef}
      sx={{ 
        p: 2, 
        minWidth: 300, 
        bgcolor: isOver ? "primary.light" : "grey.50",
        transition: "background-color 0.2s ease",
        border: isOver ? "2px solid primary.main" : "none"
      }}
    >
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        {title} ({tasks.length})
      </Typography>
      <SortableContext items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
        {tasks.map((task) => (
          <SortableTaskCard
            key={task.id}
            task={task}
            onEdit={onEdit}
            onDelete={onDelete}
            onToggleComplete={onToggleComplete}
          />
        ))}
      </SortableContext>
    </Paper>
  );
};

const Tasks: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const confirm = useConfirm();
  const { tasks, loading, error } = useAppSelector((state) => state.tasks);
  const [viewMode, setViewMode] = useState<"table" | "kanban">("table");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [searchQuery, setSearchQuery] = useState("");
  const { cases } = useSelector((state: RootState) => state.cases);
  const [users, setUsers] = useState<any[]>([]);
  const { showSuccess, showError } = useToast();

  const { cases: casesData, loading: casesLoading } = useSelector(
    (state: RootState) => state.cases,
  );

  const { user, isAuthenticated } = useSelector(
    (state: RootState) => state.auth,
  );

  // Get current user ID (you might get this from auth context)
  const getCurrentUserId = () => {
    // This should come from your auth context
    return user?.user_id?.toString() || ""; // Return as string since service expects string
  };

  const loadUsers = async () => {
    try {
      const usersData = await usersService.getUsers();
      setUsers(usersData.data);
    } catch (err: any) {
      console.error("Failed to load users:", err);
    }
  };

  // Fetch tasks on component mount and when user changes
  useEffect(() => {
    if (user?.user_id) {
      dispatch(fetchTasks(getCurrentUserId()));
      dispatch(fetchCases({ userId: user?.user_id?.toString() || "", page: 1, limit: 200 }));
      loadUsers();
    }
  }, [dispatch, user?.user_id]);

  // Clear error when component unmounts or dialog closes
  useEffect(() => {
    if (!openDialog && error) {
      dispatch(clearError());
    }
  }, [openDialog, error, dispatch]);

  const filteredTasks = tasks
    ?.filter((task) =>
      statusFilter === "all" ? true : task.status === statusFilter,
    )
    .filter((task) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        task.title?.toLowerCase().includes(q) ||
        task.description?.toLowerCase().includes(q) ||
        task.priority?.toLowerCase().includes(q) ||
        task.status?.toLowerCase().includes(q)
      );
    });

  const handleToggleComplete = async (id: number) => {
    const result = await dispatch(toggleTaskComplete(id));
    if ('error' in result) {
      showError(t('tasks.failedToToggleCompletion'));
      console.error('Toggle error:', result);
    } else {
      // Refetch tasks to get the updated list
      await dispatch(fetchTasks(getCurrentUserId()));
    }
  };

  const handleOpenDialog = (task?: Task) => {
    if (task) {
      setSelectedTask(task);
      setFormData({
        title: task.title,
        description: task.description,
        assignee_id: task.assignee_id,
        case_id: task.case_id,
        priority: task.priority,
        status: task.status,
        due_date: task.due_date,
      });
    } else {
      setSelectedTask(null);
      setFormData({
        ...initialFormData,
        assignee_id: user?.user_id?.toString() || "", // Default to current user
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTask(null);
    setFormData(initialFormData);
    dispatch(clearError());
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      showError(t('tasks.titleRequired'));
      return;
    }
    
    let result;

    if (selectedTask) {
      result = await dispatch(
        updateTask({
          id: selectedTask.id,
          title: formData.title,
          description: formData.description,
          assignee_id: formData.assignee_id,
          case_id: formData.case_id,
          priority: formData.priority,
          status: formData.status,
          due_date: formData.due_date,
        }),
      );
    } else {
      result = await dispatch(
        createTask({
          title: formData.title,
          description: formData.description,
          assignee_id: formData.assignee_id,
          case_id: formData.case_id,
          priority: formData.priority,
          status: formData.status,
          due_date: formData.due_date,
        }),
      );
    }
    
    if ('error' in result) {
      showError(result.payload as string);
      console.error(result);
    } else {
      showSuccess(t('tasks.taskSaved'));
      // Refetch tasks to get the updated list
      await dispatch(fetchTasks(getCurrentUserId()));
      // Also refetch cases if needed
      dispatch(fetchCases({ userId: user?.user_id?.toString() || "" }));
    }
    
    handleCloseDialog();
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm(t('tasks.confirmDeleteTask'));
    if (ok) {
      const result = await dispatch(deleteTask({ taskId: id, userId: getCurrentUserId() }));
      if ('error' in result) {
        showError(t('tasks.failedToDeleteTask'));
        console.error('Delete error:', result);
      } else {
        showSuccess(t('tasks.taskDeleted'));
        // Refetch tasks to get the updated list
        await dispatch(fetchTasks(getCurrentUserId()));
      }
    }
  };

  // Drag and drop handlers
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    const taskId = active.id as number;
    const newStatus = over.id as string;

    // Check if we're dropping on a valid status column
    if (newStatus === "pending" || newStatus === "in_progress" || newStatus === "completed") {
      const task = tasks.find(t => t.id === taskId);
      if (task && task.status !== newStatus) {
        try {
          const result = await dispatch(updateTaskStatus({ taskId, status: newStatus, userId: user?.user_id || '' }));
          if ('error' in result) {
            showError(t('tasks.failedToUpdateStatus'));
            console.error('Update error:', result);
          } else {
            showSuccess(t('tasks.statusUpdated'));
            // Refetch tasks to get the updated list
            await dispatch(fetchTasks(getCurrentUserId()));
          }
        } catch (error) {
          showError(t('tasks.failedToUpdateStatus'));
          console.error('Update error:', error);
        }
      }
    }
  };

  const getPriorityColor = (
    priority: string,
  ): "default" | "primary" | "warning" | "error" | "info" => {
    const map: Record<
      string,
      "default" | "primary" | "warning" | "error" | "info"
    > = { low: "info", medium: "primary", high: "warning", urgent: "error" };
    return map[priority] || "default";
  };

  const columns: GridColDef[] = [
    {
      field: "completed",
      headerName: "",
      width: 50,
      renderCell: (params: GridRenderCellParams) => (
        <Checkbox
          checked={params.row.completed}
          onChange={() => handleToggleComplete(params.row.id)}
          size="small"
        />
      ),
    },
    {
      field: "title",
      headerName: t("tasks.columnTask"),
      width: 250,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              textDecoration: params.row.completed ? "line-through" : "none",
              color: params.row.completed ? "text.secondary" : "text.primary",
            }}
          >
            {params.row.title}
          </Typography>
          {params.row.description && (
            <Typography variant="caption" color="text.secondary">
              {params.row.description.length > 50
                ? `${params.row.description.substring(0, 50)}...`
                : params.row.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "assignee",
      headerName: t("tasks.columnAssignee"),
      width: 150,
      renderCell: (params: GridRenderCellParams) =>
        params.row.assigned_to_name ||
        users.find((u) => u.user_id === params.row.assignee_id)?.full_name ||
        "-",
    },
    {
      field: "case",
      headerName: t("tasks.columnCase"),
      width: 160,
      renderCell: (params: GridRenderCellParams) =>
        params.row.case_subject ||
        cases.find((c) => c.id === params.row.case_id)?.subject ||
        "-",
    },
    {
      field: "priority",
      headerName: t("tasks.columnPriority"),
      width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.row.priority}
          color={getPriorityColor(params.row.priority)}
          size="small"
        />
      ),
    },
    {
      field: "status",
      headerName: t("tasks.columnStatus"),
      width: 140,
      renderCell: (params: GridRenderCellParams) => (
        <StatusBadge status={params.row.status} />
      ),
    },
    {
      field: "due_date",
      headerName: t("tasks.columnDueDate"),
      width: 120,
      renderCell: (params: GridRenderCellParams) =>
        params.row.due_date
          ? new Date(params.row.due_date).toLocaleDateString()
          : "-",
    },
    {
      field: "actions",
      headerName: t("tasks.columnActions"),
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title={t("common.edit")}>
            <IconButton
              size="small"
              onClick={() => handleOpenDialog(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("common.delete")}>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(params.row.id)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const pendingCount = tasks.filter((task) => !task.completed).length;
  const completedCount = tasks.filter((task) => task.completed).length;

  // Kanban columns definition
  const kanbanColumns = [
    { id: 'pending', title: t('common.pending') },
    { id: 'in_progress', title: t('tasks.inProgress') },
    { id: 'completed', title: t('tasks.completed') }
  ];

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <Typography>{t('tasks.loadingTasks')}</Typography>
      </Box>
    );
  }

  if (error && tasks.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4">{t('navigation.tasks')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('tasks.pendingCompletedSummary', {
              pending: pendingCount,
              completed: completedCount,
            })}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="table">
              <ViewListIcon />
            </ToggleButton>
            <ToggleButton value="kanban">
              <ViewKanbanIcon />
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            {t('tasks.newTask')}
          </Button>
        </Box>
      </Box>

      <Tabs
        value={statusFilter}
        onChange={(_, value) => setStatusFilter(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, minHeight: 40, "& .MuiTab-root": { minHeight: 40, textTransform: "none" } }}
      >
        <Tab value="all" label={t("common.all")} />
        {getStatusFilterOptions(t).filter((option) => option.value !== "cancelled").map((option) => (
          <Tab key={option.value} value={option.value} label={option.label} />
        ))}
      </Tabs>

      <Card sx={{ mb: 2 }}>
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            placeholder={t('tasks.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 600 }}
          />
        </Box>
      </Card>

      {error && (
        <Card sx={{ mb: 2, bgcolor: "error.light" }}>
          <Box sx={{ p: 2 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        </Card>
      )}

      {viewMode === "table" ? (
        <Card>
          <DataGrid
            rows={filteredTasks}
            columns={columns}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            pageSizeOptions={[10]}
            disableRowSelectionOnClick
            autoHeight
            rowHeight={60}
          />
        </Card>
      ) : (
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <Box sx={{ display: "flex", gap: 2, pb: 2 }}>
            {kanbanColumns?.map((column: any) => (
              <DroppableKanbanColumn
                key={column.id}
                title={column.title}
                status={column.id}
                tasks={filteredTasks.filter(
                  (task) => task.status === column.id,
                )}
                onEdit={handleOpenDialog}
                onDelete={handleDelete}
                onToggleComplete={handleToggleComplete}
              />
            ))}
          </Box>
        </DndContext>
      )}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {selectedTask ? t('tasks.editTask') : t('tasks.newTask')}
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label={t('tasks.taskTitle')}
              fullWidth
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              error={!formData.title.trim()}
              helperText={!formData.title.trim() ? t('tasks.titleRequired') : ""}
            />
            <TextField
              label={t('tasks.description')}
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label={t('tasks.assignedTo')}
                fullWidth
                value={formData.assignee_id || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    assignee_id: e.target.value,
                  })
                }
                select
              >
                {users
                  .filter(
                    (userItem) =>
                      userItem.status === "active" &&
                      userItem.firm_id === user?.firm_id,
                  )
                  .map((userItem) => (
                    <MenuItem key={userItem.id} value={userItem.user_id}>
                      {userItem.full_name}
                    </MenuItem>
                  ))}
              </TextField>
              <TextField
                label={t('billing.case')}
                fullWidth
                value={formData.case_id || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    case_id: e.target.value
                      ? parseInt(e.target.value)
                      : undefined,
                  })
                }
                select
              >
                {casesData.map((caseItem) => (
                  <MenuItem key={caseItem.id} value={caseItem.id}>
                    {caseItem.subject}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                select
                label={t('tasks.priority')}
                fullWidth
                value={formData.priority}
                onChange={(e) =>
                  setFormData({ ...formData, priority: e.target.value })
                }
              >
                <MenuItem value="low">{t('common.low')}</MenuItem>
                <MenuItem value="medium">{t('common.medium')}</MenuItem>
                <MenuItem value="high">{t('common.high')}</MenuItem>
                <MenuItem value="urgent">{t('tasks.urgent')}</MenuItem>
              </TextField>
              <TextField
                select
                label={t('common.status')}
                fullWidth
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <MenuItem value="pending">{t('common.pending')}</MenuItem>
                <MenuItem value="in_progress">{t('tasks.inProgress')}</MenuItem>
                <MenuItem value="completed">{t('tasks.completed')}</MenuItem>
              </TextField>
            </Box>
            <TextField
              label={t('tasks.dueDate')}
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.due_date}
              onChange={(e) =>
                setFormData({ ...formData, due_date: e.target.value })
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t("common.cancel").toUpperCase()}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedTask ? t("common.update").toUpperCase() : t("common.create").toUpperCase()}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Tasks;
